import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';

// --- Helper: tạo URL tuyệt đối cho ảnh ---
const createAbsoluteUrl = (path?: string | null) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (api.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}${path}`;
};

// --- Interface Cập Nhật ---
interface Article {
  slug: string;
  // ✅ Cập nhật thumbnail thành object để khớp với Backend mới
  thumbnail?: {
    url: string;
    public_id?: string;
  };
  category?: string;
  title?: string;
  publishDate?: string;
}

const TinTucTongHop = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = t('news_page.page_title');

    const fetchNews = async () => {
      try {
        setLoading(true);
        // Backend trả về data format đã xử lý ở controller (res.json({ data: formattedNews ... }))
        const { data } = await api.get(`/news?lang=${currentLang}`);

        if (data && Array.isArray(data.data)) {
          setArticles(data.data);
        } else if (Array.isArray(data)) {
          // Fallback cho trường hợp trả mảng trực tiếp
          setArticles(data);
        } else {
          console.warn('⚠️ API không trả mảng hợp lệ:', data);
        }
      } catch (error) {
        console.error('❌ Lỗi khi tải danh sách tin tức:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [currentLang, t]);

  if (loading) {
    return (
      <div className="bg-gray-50 py-16 text-center">
        <h1 className="text-2xl font-semibold">Đang tải tin tức...</h1>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* --- Tiêu đề trang --- */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">
            {t('news_page.main_title')}
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            {t('news_page.subtitle')}
          </p>
        </div>

        {/* --- Danh sách bài viết --- */}
        {articles.length > 0 ? (
          <div className="grid gap-8 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
            {articles.map((article) => {
              const publishDate = article.publishDate
                ? new Date(article.publishDate).toLocaleDateString('vi-VN')
                : '';

              // ✅ Lấy URL an toàn từ object thumbnail
              const thumbnailUrl = article.thumbnail?.url || '';

              return (
                <Link
                  to={`/${currentLang}/news/${article.slug}`}
                  key={article.slug}
                  className="group flex flex-col bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                >
                  {/* Ảnh thumbnail */}
                  <div className="flex-shrink-0 h-48 w-full bg-gray-200 overflow-hidden relative">
                    {thumbnailUrl ? (
                      <img
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        src={createAbsoluteUrl(thumbnailUrl)}
                        alt={article.title}
                        loading="lazy"
                      />
                    ) : (
                      // Placeholder nếu không có ảnh
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <span className="text-sm">No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Nội dung */}
                  <div className="p-6 flex flex-col justify-between flex-grow">
                    <div>
                      <p className="text-sm font-medium text-green-700">
                        {article.category || t('news_page.default_category', { defaultValue: 'Tin tức' })}
                      </p>
                      <p className="text-xl font-semibold text-gray-900 mt-2 group-hover:text-green-800 transition-colors line-clamp-2">
                        {article.title || '(Không có tiêu đề)'}
                      </p>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500">{publishDate}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl text-gray-500">{t('news_page.no_articles')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TinTucTongHop;